/*
 * Theme toggle — a small floating chip that switches between the dark
 * (default) and light themes by toggling the `dark` class on <html>.
 *
 * The choice persists in localStorage; the inline script in index.html
 * reapplies it before first paint so there is no flash of the wrong theme.
 * The chip lives bottom-left, where nothing else on the page ever sits
 * (the section nav owns the right edge).
 */

import type { ThemeToggleContent } from "../i18n/types";

const STORAGE_KEY = "ucloud-theme";
const THEME_COLOR = { dark: "#21262d", light: "#ffffff" };

/* Show the sun while in dark mode (click → light) and the moon while in
 * light mode (click → dark): the icon always names the destination. */
const SUN_ICON = `<svg class="themetoggle__icon themetoggle__icon--sun" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/></svg>`;
const MOON_ICON = `<svg class="themetoggle__icon themetoggle__icon--moon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>`;

function isDark(): boolean {
  return document.documentElement.classList.contains("dark");
}

/* Keep the browser UI (status bar, tab strip) in step with the theme. */
function syncThemeColorMeta(): void {
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", isDark() ? THEME_COLOR.dark : THEME_COLOR.light);
}

export function renderThemeToggle(t: ThemeToggleContent): HTMLElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "themetoggle";
  button.innerHTML = SUN_ICON + MOON_ICON;

  const sync = (): void => {
    button.setAttribute(
      "aria-label",
      isDark() ? t.toLight : t.toDark,
    );
    syncThemeColorMeta();
  };
  sync();

  button.addEventListener("click", () => {
    document.documentElement.classList.toggle("dark");
    try {
      localStorage.setItem(STORAGE_KEY, isDark() ? "dark" : "light");
    } catch {
      /* Storage can be unavailable (private mode, sandboxing) — the theme
       * still switches, it just won't persist across reloads. */
    }
    sync();
  });

  return button;
}
